// Neo's panel on the home page's iPad, playing the scripted conversation
// (TOM-980). It steps through chatScript.ts's frames on its own state, so its
// 22-34 ms updates re-render this panel only, never the page or the chart. The
// loop pauses while the hero is off screen or the tab is hidden, and resumes
// where it stopped. Under reduced motion it shows the finished conversation
// with the panel open and runs no timers.
import { useEffect, useRef, useState, type RefObject } from 'react';

import { useReducedMotion } from '../../lib/useReducedMotion';
import {
  CHAT_FINISHED,
  CHAT_SCRIPT,
  CHAT_TIMELINE,
  chatRuns,
  chatStatus,
  nextFrameIndex,
  type ChatLine,
} from './chatScript';
import { SCENE_HEADLINE } from './homeScene';

function Bubble({ from, text }: ChatLine) {
  return (
    <div className={`home-msg home-msg--${from}`}>
      <div className="home-msg__bubble">{text}</div>
    </div>
  );
}

export function HomeChat({ heroRef }: { heroRef: RefObject<HTMLElement | null> }) {
  const reducedMotion = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [onScreen, setOnScreen] = useState(false);
  const [tabHidden, setTabHidden] = useState(
    () => typeof document !== 'undefined' && document.hidden
  );
  // What was left of the current frame when the loop paused.
  const remainingMs = useRef<number | null>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setOnScreen(true);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) setOnScreen(entry.isIntersecting);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [heroRef]);

  useEffect(() => {
    const onChange = () => setTabHidden(document.hidden);
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);

  const runs = chatRuns({ onScreen, tabHidden, reducedMotion });

  useEffect(() => {
    if (!runs) return;
    const wait = remainingMs.current ?? CHAT_TIMELINE[index]!.holdMs;
    remainingMs.current = null;
    const startedAt = performance.now();
    let fired = false;
    const timer = window.setTimeout(() => {
      fired = true;
      setIndex((i) => nextFrameIndex(i, CHAT_TIMELINE.length));
    }, wait);
    return () => {
      window.clearTimeout(timer);
      if (!fired) remainingMs.current = Math.max(0, wait - (performance.now() - startedAt));
    };
  }, [runs, index]);

  const frame = reducedMotion ? CHAT_FINISHED : CHAT_TIMELINE[index]!;

  return (
    <div className={`home-chat${frame.open ? ' home-chat--open' : ''}`}>
      <div className="home-chat__head">
        <span className="home-avatar home-avatar--chat">N</span>
        <span className="home-chat__who">
          <span className="home-chat__name">Neo</span>
          <span className="home-chat__status">{chatStatus(frame, SCENE_HEADLINE)}</span>
        </span>
      </div>
      <div className="home-chat__thread">
        {CHAT_SCRIPT.slice(0, frame.shown).map((line, i) => (
          <Bubble key={i} from={line.from} text={line.text} />
        ))}
        {frame.typing !== null ? <Bubble from="neo" text={frame.typing} /> : null}
        {frame.thinking ? (
          <div className="home-msg home-msg--neo">
            <div className="home-dots">
              <span />
              <span />
              <span />
            </div>
          </div>
        ) : null}
      </div>
      <div className="home-chat__box">
        <span className={`home-chat__draft${frame.draft ? ' home-chat__draft--typed' : ''}`}>
          {frame.draft || 'Ask Neo anything…'}
        </span>
        <span className="home-chat__send">
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M5 12h14" />
            <path d="M13 6l6 6-6 6" />
          </svg>
        </span>
      </div>
    </div>
  );
}
